// GumDiseaseData.ts

interface MetaData {
    title: string;
    description: string;
    robots?: string;
    canonical?: string;
    og?: {
        locale?: string;
        type?: string;
        title?: string;
        description?: string;
        url?: string;
        site_name?: string;
        updated_time?: string;
        image?: string;
        image_secure_url?: string;
        image_width?: number;
        image_height?: number;
        image_alt?: string;
        image_type?: string;
        video?: string;
        video_duration?: number;
        video_upload_date?: string;
        video_allow_embed?: boolean;
    };
    twitter?: {
        card?: string;
        title?: string;
        description?: string;
        image?: string;
    };
}

interface Step {
    title: string;
    desc: string;
    icon: string;
}

interface ProcedureSteps {
    heading: string;
    para: string;
    steps: Step[];
}

interface ListItem {
    title: string;
    desc: string;
    icon: string;
}

interface ListSection {
    heading?: string;
    subheading?: string;
    para?: string;
    items: ListItem[];
}

interface FAQItem {
    question: string;
    answer: string;
}

interface FAQSection {
    heading?: string;
    subheading?: string;
    items: FAQItem[];
}

interface HeroSection {
    title: string;
    subtitle: string;
    description: string;
}

interface HowItWorks {
    title: string;
    description: string;
}

interface TreatmentArea {
    ar: string;
    title: string;
    image: string;
}

interface LanguageContent {
    url?: string;
    meta: MetaData;
    breadcrumbs: string[];
    hero: HeroSection;
    howItWorks: HowItWorks;
    procedureSteps: ProcedureSteps;
    benefits: ListSection;
    eligibility: ListSection;
    preparationTips: ListSection;
    results: ListSection;
    aftercareTips: ListSection;
    faqData: FAQSection;
    treatmentAreas1: TreatmentArea[];
    treatmentAreas2: TreatmentArea[];
}

interface SharedData {
    images: {
        main: string;
        beforeAfter: string[];
        introductionImage?: string;
    };
    product: {
        id: number;
        title: string;
        price: number;
        description: string;
    };
}

export const gumDisease: {
    en: LanguageContent;
    ar: LanguageContent;
    shared: SharedData;
} =
{
    en: {
        "url": "",
        "meta": {
            "title": "Gum Disease Laser Treatment",
            "description": "Gentle Precision, Lasting Gum Health with Laser Treatment for periodontal disease. Minimally invasive procedure with faster healing.",
            "robots": "",
            "canonical": "",
            "og": {}
        },
        "breadcrumbs": ["Home", "Services", "Gum Disease Laser Treatment"],
        "hero": {
            "title": "Gum Disease Laser Treatment",
            "subtitle": "Gentle Precision, Lasting Gum Health with Laser Treatment",
            "description": "Gum disease, also known as periodontal disease, is a common oral condition that affects the health of your gums and teeth. Left untreated, it can lead to tooth loss, infections, and other serious health issues. However, advancements in dental technology have introduced Gum Disease Laser Treatment, a minimally invasive and highly effective procedure that targets and treats gum disease with precision."
        },
        "howItWorks": {
            "title": "What are the benefits of gum disease laser treatment?",
            "description": "Gum Disease Laser Treatment — latest non-surgical treatment options using lasers to remove infected tissue, kill bacteria, promote healing, and stimulate tissue regeneration. Not only is this method less painful than traditional gum surgery, but it preserves healthy tissue allowing quicker recovery periods."
        },
        "procedureSteps": {
            "heading": "The Procedure: Five Steps To Treat Gum Disease",
            "para": "Laser Treatment for Gum Disease: The Procedure The laser treatment for gum disease is planned with an aim to provide effective and painless therapy to the patients. Here is a guide on what such a process looks like, step-by-step, explaining what patients can expect during their treatment.",
            "steps": [
                {
                    "title": "Initial Consultation",
                    "desc": "The dentist then checks how healthy your mouth is, with a full check on your gums to see how far the gum disease actually is.",
                    "icon": "fa-solid fa-calendar-check"
                },
                {
                    "title": "Laser Treatment",
                    "desc": "In this process, a specialized dental laser is utilized to extract inflamed gum tissue and eliminate bacteria in the gum pockets.",
                    "icon": "fa-solid fa-light-emergency"
                },
                {
                    "title": "Cleaning the Gum Pockets",
                    "desc": "The dentist scrapes the tooth surfaces, removing tartar and dental plaque from the tooth roots.",
                    "icon": "fa-solid fa-broom"
                },
                {
                    "title": "Promoting Healing",
                    "desc": "This helps to reattach healthy tissue to the tooth, leading to faster healing and shallower pocket depths.",
                    "icon": "fa-solid fa-heart-circle-plus"
                }
            ]
        },
        "benefits": {
            "heading": "Benefits",
            "subheading": "Gum disease laser treatment has several benefits as compared to traditional treatment methods",
            "para": "",
            "items": [
                {
                    "title": "Minimally Invasive",
                    "desc": "Being incision and stitch free, laser therapy reduces discomfort and downtime.",
                    "icon": "fa-solid fa-hand-sparkles"
                },
                {
                    "title": "Reduced Bleeding and Swelling",
                    "desc": "The focus of the laser reduces bleeding and swelling both during and after the procedure.",
                    "icon": "fa-solid fa-droplet-slash"
                },
                {
                    "title": "Faster Healing Time",
                    "desc": "Patients recover faster than conventional surgical techniques.",
                    "icon": "fa-solid fa-clock-rotate-left"
                },
                {
                    "title": "Lower Risk of Infection",
                    "desc": "The laser also helps to sterilize the area, decreasing the chances of infection when the area heals.",
                    "icon": "fa-solid fa-shield-virus"
                },
                {
                    "title": "Targeted Treatment",
                    "desc": "It can be targeted precisely at diseased tissue, preserving surrounding healthy tissue and enhancing oral health in general.",
                    "icon": "fa-solid fa-bullseye"
                },
                {
                    "title": "Stimulates Bone and Tissue Regrowth",
                    "desc": "Laser therapy promotes the regrowth of bone and gum tissue that could have been damaged from gum disease.",
                    "icon": "fa-solid fa-leaf"
                }
            ]
        },
        "eligibility": {
            "heading": "Eligibility",
            "subheading": "Gum disease laser treatment may not be suitable for everyone",
            "para": "But this option is appropriate for many, especially those who desire an alternative to greatest surgeries. A below, who is a good candidate for this procedure.",
            "items": [
                {
                    "title": "People with moderate to severe gum disease",
                    "desc": "",
                    "icon": "fa-solid fa-user-injured"
                },
                {
                    "title": "Patients looking for a less invasive treatment alternative",
                    "desc": "",
                    "icon": "fa-solid fa-user-shield"
                },
                {
                    "title": "Individuals with anxiety concerning conventional dental surgery",
                    "desc": "",
                    "icon": "fa-solid fa-head-side-virus"
                },
                {
                    "title": "People with gum inflammation and deep periodontal pockets",
                    "desc": "",
                    "icon": "fa-solid fa-teeth-open"
                },
                {
                    "title": "Patients who wish to save as much natural tissue as possible",
                    "desc": "",
                    "icon": "fa-solid fa-hand-holding-medical"
                }
            ]
        },
        "preparationTips": {
            "heading": "Preparation",
            "subheading": "Preparing for gum disease laser treatment requires several straightforward steps",
            "para": "Patients should pay close attention to their dentist's instructions for the optimal outcome.",
            "items": [
                {
                    "title": "Oral Examination",
                    "desc": "Have your teeth and gums examined in detail by your dentist (6 months or year before the operation)",
                    "icon": "fa-solid fa-teeth"
                },
                {
                    "title": "Pre-Procedural Cleaning",
                    "desc": "Your dentist might suggest that you undergo a professional cleaning session prior to the laser treatment to eliminate all plaque buildup.",
                    "icon": "fa-solid fa-broom"
                },
                {
                    "title": "Health Evaluation",
                    "desc": "If you have health conditions or take medications, tell your dentist to prevent complications.",
                    "icon": "fa-solid fa-clipboard-user"
                },
                {
                    "title": "Follow Pre-Treatment Guidelines",
                    "desc": "Patients should follow any dietary or lifestyle changes recommended by their dentist prior to their procedure.",
                    "icon": "fa-solid fa-list-check"
                }
            ]
        },
        "results": {
            "heading": "Results",
            "subheading": "Laser treatment for gum disease - Reducing the impact of gum disease",
            "para": "Results can be long-lasting and are far better when paired with good oral hygiene and regular dentist visits.",
            "items": [
                {
                    "title": "Reduction in Gum Inflammation",
                    "desc": "Patients often experience a dramatic reduction in gum inflammation and bleeding.",
                    "icon": "fa-solid fa-fire-flame-curved"
                },
                {
                    "title": "Decreased Pocket Depths",
                    "desc": "The depth of the periodontal pockets is decreased and the accumulation of the bacteria is thereby prevented.",
                    "icon": "fa-solid fa-ruler-combined"
                },
                {
                    "title": "Healthier Gums and Teeth",
                    "desc": "Laser therapy assists in restoring healthy gums while lowering the risk of losing teeth.",
                    "icon": "fa-solid fa-tooth"
                },
                {
                    "title": "Improved Overall Oral Health",
                    "desc": "It maintains a cleaner, healthier mouth, and overall health is improved.",
                    "icon": "fa-solid fa-face-smile"
                }
            ]
        },
        "aftercareTips": {
            "heading": "",
            "subheading": "",
            "para": "",
            "items": []
        },
        "faqData": {
            "heading": "Frequently Asked Questions",
            "subheading": "",
            "items": [
                {
                    "question": "Is the procedure painful?",
                    "answer": "No, gum disease laser treatment is generally painless. Most patients report minimal discomfort during the procedure, and local anesthesia is often used to numb the area."
                },
                {
                    "question": "How long does the procedure take?",
                    "answer": "The length of the treatment depends on the severity of the gum disease, but it typically takes between one to two hours."
                },
                {
                    "question": "What is the recovery time?",
                    "answer": "Recovery is much faster compared to traditional gum surgery, with most patients returning to normal activities within a few days."
                },
                {
                    "question": "Are there any side effects?",
                    "answer": "Mild discomfort, swelling, and sensitivity may occur after the procedure, but these effects usually subside within a few days."
                }
            ]
        },
        "treatmentAreas1": [],
        "treatmentAreas2": []
    },
    ar: {
        "url": "",
        "meta": {
            "title": "علاج أمراض اللثة بالليزر",
            "description": "دقة وصحة لثة تدوم مع علاج اللثة بالليزر. تقنية دقيقة وفعالة لعلاج أمراض اللثة بأقل تدخل جراحي.",
            "robots": "",
            "canonical": "",
            "og": {}
        },
        "breadcrumbs": ["الرئيسية", "الخدمات", "علاج أمراض اللثة بالليزر"],
        "hero": {
            "title": "علاج أمراض اللثة بالليزر",
            "subtitle": "دقة وصحة لثة تدوم مع علاج اللثة بالليزر",
            "description": "تُعرف أمراض اللثة أيضاً باسم أمراض دواعم الأسنان، وهي من الأمراض الشائعة التي تصيب اللثة والأسنان، وقد تؤدي إلى فقدان الأسنان، وظهور التهابات، ومضاعفات صحية أخرى أكثر خطورة إذا تُركت دون علاج. لكن بفضل التقدم الحديث في تقنيات طب الأسنان، أصبح بالإمكان علاج أمراض اللثة باستخدام الليزر، وهي تقنية دقيقة وفعالة تستهدف الأنسجة المصابة وتعالجها بأقل قدر من التدخل الجراحي."
        },
        "howItWorks": {
            "title": "ما هي فوائد علاج اللثة بالليزر؟",
            "description": "يُعد علاج اللثة بالليزر من أحدث الخيارات غير الجراحية، حيث يعمل على إزالة الأنسجة المصابة، والقضاء على البكتيريا، وتعزيز عملية الشفاء، وتحفيز تجدد الأنسجة. يمتاز هذا العلاج بأنه أقل ألماً مقارنةً بالعمليات الجراحية التقليدية، كما يحافظ على الأنسجة السليمة، مما يساهم في تسريع فترة التعافي."
        },
        "procedureSteps": {
            "heading": "خطوات علاج أمراض اللثة بالليزر",
            "para": "يُعد علاج اللثة بالليزر خيارًا فعالًا وخاليًا من الألم نسبيًا. فيما يلي دليل تفصيلي حول خطوات العلاج وما يمكن أن يتوقعه المريض أثناء الجلسة:",
            "steps": [
                {
                    "title": "استشارة أولية",
                    "desc": "يفحص طبيب الأسنان صحة الفم بالكامل، مع التركيز على تقييم حالة اللثة لتحديد مدى تطور مرض اللثة.",
                    "icon": "fa-solid fa-calendar-check"
                },
                {
                    "title": "العلاج بالليزر",
                    "desc": "يُستخدم ليزر متخصص للأسنان لإزالة أنسجة اللثة الملتهبة وللتخلص من البكتيريا المتراكمة في الجيوب اللثوية.",
                    "icon": "fa-solid fa-light-emergency"
                },
                {
                    "title": "تنظيف الجيوب اللثوية",
                    "desc": "يقوم الطبيب بكشط وتنظيف سطح الأسنان لإزالة الجير والترسبات العالقة في جذور الأسنان.",
                    "icon": "fa-solid fa-broom"
                },
                {
                    "title": "تعزيز عملية الشفاء",
                    "desc": "يساعد هذا الإجراء على إعادة التصاق أنسجة اللثة الصحية بالسن، مما يسرع عملية الشفاء ويقلل من عمق الجيوب اللثوية.",
                    "icon": "fa-solid fa-heart-circle-plus"
                }
            ]
        },
        "benefits": {
            "heading": "الفوائد",
            "subheading": "يتميز علاج أمراض اللثة بالليزر بعدة فوائد مقارنةً بالطرق التقليدية",
            "para": "",
            "items": [
                {
                    "title": "إجراء غير جراحي",
                    "desc": "لا يتطلب شقوقاً أو غرزاً، مما يخفف من الانزعاج ويقلل فترة التوقف عن الأنشطة اليومية.",
                    "icon": "fa-solid fa-hand-sparkles"
                },
                {
                    "title": "نزيف وتورم أقل",
                    "desc": "يساهم تركيز الليزر في تقليل النزيف والتورم أثناء وبعد الإجراء.",
                    "icon": "fa-solid fa-droplet-slash"
                },
                {
                    "title": "تعافٍ أسرع",
                    "desc": "يتعافى المريض بشكل أسرع مقارنةً بالطرق الجراحية التقليدية.",
                    "icon": "fa-solid fa-clock-rotate-left"
                },
                {
                    "title": "تقليل خطر العدوى",
                    "desc": "يساعد الليزر في تعقيم المنطقة المعالجة، مما يقلل من احتمالية حدوث عدوى أثناء مرحلة الشفاء.",
                    "icon": "fa-solid fa-shield-virus"
                },
                {
                    "title": "علاج موجه ودقيق",
                    "desc": "يستهدف الأنسجة المصابة بدقة، مع الحفاظ على الأنسجة السليمة المحيطة، ما يعزز صحة الفم بشكل عام.",
                    "icon": "fa-solid fa-bullseye"
                },
                {
                    "title": "تحفيز نمو العظام والأنسجة",
                    "desc": "يعمل الليزر على تعزيز إعادة نمو أنسجة اللثة والعظم التي قد تكون تضررت بسبب أمراض اللثة.",
                    "icon": "fa-solid fa-leaf"
                }
            ]
        },
        "eligibility": {
            "heading": "من المؤهل لعلاج أمراض اللثة بالليزر؟",
            "subheading": "قد لا يناسب علاج اللثة بالليزر للجميع",
            "para": "ولكنه يُعد خياراً مثالياً للعديد من المرضى، خاصةً الذين يبحثون عن بديل أقل تدخلاً للجراحة التقليدية. فيما يلي الحالات التي تُعد مرشحة جيدة لهذا الإجراء.",
            "items": [
                {
                    "title": "الأشخاص المصابون بأمراض اللثة بدرجات متوسطة إلى شديدة",
                    "desc": "",
                    "icon": "fa-solid fa-user-injured"
                },
                {
                    "title": "المرضى الذين يبحثون عن خيار علاجي غير جراحي",
                    "desc": "",
                    "icon": "fa-solid fa-user-shield"
                },
                {
                    "title": "الأفراد الذين يشعرون بالقلق أو الخوف من الجراحة التقليدية",
                    "desc": "",
                    "icon": "fa-solid fa-head-side-virus"
                },
                {
                    "title": "الأشخاص الذين يعانون من التهاب اللثة وتكوّن جيوب لثوية عميقة",
                    "desc": "",
                    "icon": "fa-solid fa-teeth-open"
                },
                {
                    "title": "المرضى الذين يفضلون الحفاظ على أكبر قدر ممكن من الأنسجة الطبيعية",
                    "desc": "",
                    "icon": "fa-solid fa-hand-holding-medical"
                }
            ]
        },
        "preparationTips": {
            "heading": "الاستعداد",
            "subheading": "تتطلب عدة خطوات بسيطة للتحضير لعلاج اللثة بالليزر",
            "para": "لضمان سلاسة الإجراء وتحقيق أفضل النتائج.",
            "items": [
                {
                    "title": "فحص شامل للفم",
                    "desc": "يجب إجراء فحص دقيق للأسنان واللثة لدى طبيب الأسنان، ويفضل أن يتم ذلك قبل الإجراء بستة أشهر أو سنة.",
                    "icon": "fa-solid fa-teeth"
                },
                {
                    "title": "تنظيف الأسنان قبل الإجراء",
                    "desc": "قد يوصي الطبيب بجلسة تنظيف احترافية قبل موعد العلاج لإزالة الترسبات والجير المتراكم.",
                    "icon": "fa-solid fa-broom"
                },
                {
                    "title": "تقييم الحالة الصحية",
                    "desc": "إذا كنت تعاني من أي حالات صحية مزمنة أو تتناول أدوية معينة، يجب إبلاغ الطبيب لتجنب أي مضاعفات محتملة.",
                    "icon": "fa-solid fa-clipboard-user"
                },
                {
                    "title": "اتباع التعليمات الخاصة قبل العلاج",
                    "desc": "يجب على المريض الالتزام بأي تعليمات غذائية أو متعلقة بنمط الحياة يوصي بها الطبيب قبل الخضوع للإجراء.",
                    "icon": "fa-solid fa-list-check"
                }
            ]
        },
        "results": {
            "heading": "النتائج",
            "subheading": "يُعد علاج اللثة بالليزر وسيلة فعالة للتقليل من آثار أمراض اللثة",
            "para": "وتكون نتائجه طويلة الأمد، خاصةً عند الالتزام بالنظافة الجيدة للفم وزيارة طبيب الأسنان بانتظام.",
            "items": [
                {
                    "title": "تخفيف التهاب اللثة",
                    "desc": "يلاحظ المرضى غالباً انخفاضاً ملحوظاً في التورم ونزيف اللثة بعد العلاج.",
                    "icon": "fa-solid fa-fire-flame-curved"
                },
                {
                    "title": "انخفاض عمق الجيوب اللثوية",
                    "desc": "يقل عمق الجيوب حول الأسنان، مما يمنع تراكم البكتيريا.",
                    "icon": "fa-solid fa-ruler-combined"
                },
                {
                    "title": "لثة وأسنان أكثر صحة",
                    "desc": "يساهم الليزر في استعادة صحة اللثة والحد من خطر فقدان الأسنان.",
                    "icon": "fa-solid fa-tooth"
                },
                {
                    "title": "يعزز الصحة العامة للفم",
                    "desc": "يؤدي العلاج إلى فم أنظف وأكثر صحة، مما ينعكس إيجاباً على الصحة العامة.",
                    "icon": "fa-solid fa-face-smile"
                }
            ]
        },
        "aftercareTips": {
            "heading": "",
            "subheading": "",
            "para": "",
            "items": []
        },
        "faqData": {
            "heading": "الأسئلة الشائعة",
            "subheading": "",
            "items": [
                {
                    "question": "هل العلاج مؤلم؟",
                    "answer": "لا، علاج اللثة بالليزر غير مؤلم بشكل عام. معظم المرضى يبلغون عن انزعاج بسيط أثناء الإجراء، وغالباً ما يتم استخدام التخدير الموضعي لتخدير المنطقة."
                },
                {
                    "question": "كم تستغرق مدة العلاج؟",
                    "answer": "تعتمد مدة العلاج على شدة حالة اللثة، ولكنها تستغرق عادةً ما بين ساعة إلى ساعتين."
                },
                {
                    "question": "ما هي مدة التعافي؟",
                    "answer": "يكون التعافي أسرع بكثير مقارنةً بجراحة اللثة التقليدية، حيث يعود معظم المرضى إلى أنشطتهم الطبيعية خلال بضعة أيام."
                },
                {
                    "question": "هل هناك أي آثار جانبية؟",
                    "answer": "قد يحدث انزعاج بسيط، وتورم، وحساسية بعد الإجراء، ولكن هذه الآثار تزول عادةً خلال بضعة أيام."
                }
            ]
        },
        "treatmentAreas1": [],
        "treatmentAreas2": []
    },
    shared: {
        images: {
            introductionImage: "",
            main: "https://dev-elite.s3.me-central-1.amazonaws.com/wp-content/uploads/2024/10/New-Service-Baszfdvnestregner-Image-scaled.jpg",
            beforeAfter: [
                "https://dev-elite.s3.me-central-1.amazonaws.com/wp-content/uploads/2024/10/Before-After-Seretgervice-Images-10.jpg",
                "https://dev-elite.s3.me-central-1.amazonaws.com/wp-content/uploads/2024/10/Before-Afteryrr-Service-Images-9.jpg",
                "https://dev-elite.s3.me-central-1.amazonaws.com/wp-content/uploads/2024/10/Before-After-Service-Ite54emages-8.jpg"
            ]
        },
        product: {
            id: 1,
            title: "Gum Disease Laser Treatment",
            price: 500,
            description: "Gentle Precision, Lasting Gum Health with Laser Treatment for periodontal disease. Minimally invasive procedure with faster healing."
        }
    }
};
